package fyi.moko.hotpl.ui.friends

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fyi.moko.hotpl.domain.model.Friend
import fyi.moko.hotpl.domain.model.FriendSharedPlace
import kotlinx.coroutines.flow.map
import fyi.moko.hotpl.domain.usecase.ObservePlacesUseCase
import fyi.moko.hotpl.domain.usecase.ObserveVisitSummariesUseCase
import fyi.moko.hotpl.domain.service.MutualWish
import fyi.moko.hotpl.domain.service.MutualWishKey
import fyi.moko.hotpl.domain.service.mutualWishKeyOf
import fyi.moko.hotpl.domain.service.findMutualWishes
import kotlinx.coroutines.flow.combine
import fyi.moko.hotpl.domain.model.Visibility
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.domain.model.InviteCode
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.usecase.CreateInviteCodeUseCase
import fyi.moko.hotpl.domain.usecase.ObserveFriendsSharedPlacesUseCase
import fyi.moko.hotpl.domain.usecase.ObserveFriendsUseCase
import fyi.moko.hotpl.domain.usecase.RedeemInviteCodeUseCase
import fyi.moko.hotpl.domain.usecase.RedeemInviteRejection
import fyi.moko.hotpl.domain.usecase.RedeemInviteResult
import fyi.moko.hotpl.domain.usecase.RemoveFriendUseCase
import fyi.moko.hotpl.domain.usecase.SavePlaceRejection
import fyi.moko.hotpl.domain.usecase.SavePlaceResult
import fyi.moko.hotpl.domain.usecase.SavePlaceUseCase
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * ViewModel for the Friends tab (Phase 3 Social MVP).
 *
 * The social surface is online-only: friends stream live from Firestore and a
 * transport failure degrades to an empty list plus a snackbar message, never a
 * crash. Unlike the journal tabs this ViewModel has no [SyncCoordinator]
 * dependency — nothing here touches the sync queue.
 */
class FriendsViewModel(
    private val createInviteCode: CreateInviteCodeUseCase,
    private val redeemInviteCode: RedeemInviteCodeUseCase,
    observeFriends: ObserveFriendsUseCase,
    private val removeFriendUseCase: RemoveFriendUseCase,
    observeFriendsSharedPlaces: ObserveFriendsSharedPlacesUseCase,
    private val savePlaceUseCase: SavePlaceUseCase,
    observePlaces: ObservePlacesUseCase,
    observeVisitSummaries: ObserveVisitSummariesUseCase,
) : ViewModel() {

    private val _myInviteCode = MutableStateFlow<InviteCode?>(null)
    val myInviteCode: StateFlow<InviteCode?> = _myInviteCode.asStateFlow()

    private val _inviteCodeLoading = MutableStateFlow(false)
    val inviteCodeLoading: StateFlow<Boolean> = _inviteCodeLoading.asStateFlow()

    val friends: StateFlow<List<Friend>> = observeFriends()
        .catch { e ->
            if (e is CancellationException) throw e
            _uiMessage.value = "Friends aren't available right now — check your connection"
            emit(emptyList())
        }
        .stateIn(viewModelScope, SharingStarted.Lazily, emptyList())

    private val _redeemInput = MutableStateFlow("")
    val redeemInput: StateFlow<String> = _redeemInput.asStateFlow()

    private val _redeemInProgress = MutableStateFlow(false)
    val redeemInProgress: StateFlow<Boolean> = _redeemInProgress.asStateFlow()

    /** The friend whose shared places are currently expanded, or null. */
    private val _expandedFriendUid = MutableStateFlow<String?>(null)
    val expandedFriendUid: StateFlow<String?> = _expandedFriendUid.asStateFlow()

    /**
     * Every friend's shared places, live (one listener per friend, see
     * [ObserveFriendsSharedPlacesUseCase]). A friend absent from the map hasn't delivered a
     * first snapshot yet — that is the whole loading state ([sharedPlacesLoading]).
     */
    val sharedPlaces: StateFlow<Map<String, List<FriendSharedPlace>>> = observeFriendsSharedPlaces()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyMap())

    val sharedPlacesLoading: StateFlow<Set<String>> = combine(friends, sharedPlaces) { friends, shared ->
        friends.mapNotNullTo(HashSet()) { friend -> friend.uid.takeIf { it !in shared } }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptySet())

    /** placeId of a friend's shared place currently being saved into the local journal. */
    private val _addToJournalInProgress = MutableStateFlow<Set<String>>(emptySet())
    val addToJournalInProgress: StateFlow<Set<String>> = _addToJournalInProgress.asStateFlow()

    /** The friend's place open in the preview screen (0-36 d), or null for the list. */
    private val _previewPlace = MutableStateFlow<FriendSharedPlace?>(null)
    val previewPlace: StateFlow<FriendSharedPlace?> = _previewPlace.asStateFlow()

    /**
     * [MutualWishKey]s of my own journal, so the preview can say "In your journal" instead of
     * offering to add a place I already keep. Normalized name + exact coordinates, and name
     * alone when *both* sides lack coordinates — "Add to Want to go" copies null coordinates
     * verbatim, so without that relaxation the button would mint an exact duplicate of a
     * coordinate-less place (Cafe Concerto, 0-37). Same key the mutual-wish group uses.
     */
    val journalKeys: StateFlow<Set<MutualWishKey>> = observePlaces()
        .map { places -> places.mapNotNull { mutualWishKeyOf(it.name, it.latitude, it.longitude) }.toSet() }
        .catch { emit(emptySet()) }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptySet())

    /**
     * Places a friend and I both want to go to (0-37 stage 1). Live on both sides: a friend
     * sharing a place, or me logging a visit, moves a card in or out without a reload.
     * Empty offline, like the rest of the social surface.
     */
    val mutualWishes: StateFlow<List<MutualWish>> = combine(
        friends,
        sharedPlaces,
        observePlaces().catch { emit(emptyList()) },
        observeVisitSummaries().catch { emit(emptyList()) },
    ) { friends, shared, places, summaries ->
        findMutualWishes(
            friends = friends,
            sharedPlaces = shared,
            myPlaces = places,
            myVisitCounts = summaries.associate { it.placeId to it.visitCount },
        )
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    /**
     * True once every current friend's listener has delivered a first snapshot — until then
     * an empty [mutualWishes] means "still loading", not "nothing".
     */
    val mutualWishesReady: StateFlow<Boolean> = combine(friends, sharedPlaces) { friends, shared ->
        friends.isNotEmpty() && friends.all { it.uid in shared }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), false)

    private val _uiMessage = MutableStateFlow<String?>(null)
    val uiMessage: StateFlow<String?> = _uiMessage.asStateFlow()

    init {
        loadInviteCode(regenerate = false)
    }

    fun clearUiMessage() {
        _uiMessage.value = null
    }

    fun regenerateInviteCode() = loadInviteCode(regenerate = true)

    private fun loadInviteCode(regenerate: Boolean) {
        viewModelScope.launch {
            _inviteCodeLoading.value = true
            try {
                _myInviteCode.value = createInviteCode(regenerate)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                println("FriendsViewModel: invite code load failed: ${e.message ?: e::class.simpleName}")
                _uiMessage.value = "Couldn't load your invite code — check your connection"
            } finally {
                _inviteCodeLoading.value = false
            }
        }
    }

    fun onRedeemInputChange(value: String) {
        _redeemInput.value = value
    }

    fun redeemCode() {
        val code = _redeemInput.value
        if (code.isBlank() || _redeemInProgress.value) return
        viewModelScope.launch {
            _redeemInProgress.value = true
            try {
                when (val result = redeemInviteCode(code)) {
                    is RedeemInviteResult.Redeemed -> {
                        _redeemInput.value = ""
                        _uiMessage.value = "You're now friends with ${result.friend.displayName}"
                    }
                    is RedeemInviteResult.Rejected -> {
                        _uiMessage.value = when (result.reason) {
                            RedeemInviteRejection.InvalidCode -> "That invite code doesn't exist"
                            RedeemInviteRejection.SelfFriend -> "That's your own invite code"
                            RedeemInviteRejection.AlreadyFriends -> "You're already friends"
                        }
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                println("FriendsViewModel: redeem failed: ${e.message ?: e::class.simpleName}")
                _uiMessage.value = "Couldn't add friend — check your connection"
            } finally {
                _redeemInProgress.value = false
            }
        }
    }

    fun openPreview(place: FriendSharedPlace) {
        _previewPlace.value = place
    }

    fun closePreview() {
        _previewPlace.value = null
    }

    fun isInJournal(place: FriendSharedPlace): Boolean =
        mutualWishKeyOf(place.name, place.latitude, place.longitude)?.let { it in journalKeys.value } == true

    /** A mutual-wish card tap (0-37): open the friend's preview of that place. */
    fun openMutualWish(wish: MutualWish) {
        _expandedFriendUid.value = wish.friends.first().uid
        _previewPlace.value = wish.place
    }

    fun onFriendClick(friend: Friend) {
        if (_expandedFriendUid.value == friend.uid) {
            _expandedFriendUid.value = null
            _previewPlace.value = null
            return
        }
        _expandedFriendUid.value = friend.uid
    }

    fun removeFriend(friend: Friend) {
        viewModelScope.launch {
            try {
                removeFriendUseCase(friend.friendshipId)
                // The friends stream drops them and their listener with it — nothing cached here.
                if (_expandedFriendUid.value == friend.uid) _expandedFriendUid.value = null
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                println("FriendsViewModel: unfriend failed: ${e.message ?: e::class.simpleName}")
                _uiMessage.value = "Couldn't remove friend — check your connection"
            }
        }
    }

    /**
     * Saves a friend's shared place into the local journal as **my own wish** (0-36 d): name,
     * tags, coordinates and the business contact come across; the friend's status and heart
     * do not — I haven't been there, so it lands as WISHLIST, unliked, PRIVATE. Their photos
     * are viewed in the preview, never copied into my store. Mints a fresh id (a distinct
     * document I now own, not a reference to theirs) and — matching Directory import's
     * existing behavior — skips the duplicate-name warning.
     */
    fun addToJournal(place: FriendSharedPlace) {
        if (place.placeId in _addToJournalInProgress.value) return
        viewModelScope.launch {
            _addToJournalInProgress.update { it + place.placeId }
            try {
                when (val result = savePlaceUseCase(place.toJournalPlace(), saveAnyway = true)) {
                    is SavePlaceResult.Saved -> _uiMessage.value = "Saved to journal"
                    SavePlaceResult.DuplicateNameWarning -> _uiMessage.value = "Already in journal"
                    is SavePlaceResult.Rejected -> {
                        _uiMessage.value = when (result.reason) {
                            SavePlaceRejection.EmptyName -> "Couldn't save — name is required"
                        }
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                println("FriendsViewModel: add to journal failed: ${e.message ?: e::class.simpleName}")
                _uiMessage.value = "Couldn't save — check your connection"
            } finally {
                _addToJournalInProgress.update { it - place.placeId }
            }
        }
    }

    private fun FriendSharedPlace.toJournalPlace(): Place = Place(
        name = name,
        latitude = latitude,
        longitude = longitude,
        tags = tags,
        status = PlaceStatus.WISHLIST,
        liked = false,
        visibility = Visibility.PRIVATE,
        phoneNumber = phoneNumber,
        website = website,
    )
}
